/**
 * <medcline-cart-drawer> — global cart drawer custom element.
 *
 * Listens for:
 *  - 'cart:open' event       → fetch /cart.js, render, open dialog
 *  - 'cart:updated' event    → re-render with provided cart (no fetch)
 *  - [data-action="cart-open"]  click anywhere in the document → opens
 *  - [data-action="cart-close"] click inside the drawer → closes
 *  - submit on any <form action="/cart/add">  → intercepts, adds via fetch, opens drawer
 *
 * Emits:
 *  - 'cart:updated' (detail = cart object) whenever it mutates the cart
 */
class MedClineCartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-cart-dialog]');
    this.body = this.querySelector('[data-cart-body]');
    this.empty = this.querySelector('[data-cart-empty]');
    this.itemsEl = this.querySelector('[data-cart-items]');
    this.footer = this.querySelector('[data-cart-foot]');
    this.subtotalEl = this.querySelector('[data-cart-subtotal]');
    this.countTextEl = this.querySelector('[data-cart-count-text]');
    this.progressEl = this.querySelector('[data-progress]');
    this.progressFill = this.querySelector('[data-progress-fill]');
    this.progressLabel = this.querySelector('[data-progress-label]');
    this.template = this.querySelector('[data-line-template]');

    this.currency = this.dataset.currency || 'GBP';
    this.locale = this.dataset.locale || document.documentElement.lang || 'en';
    this.freeThresholdCents = Number(this.dataset.freeThreshold || 0);
    this.formatter = new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency,
    });
    const stringsEl = this.querySelector('[data-cart-drawer-strings]');
    try {
      this.strings = stringsEl ? JSON.parse(stringsEl.textContent) : {};
    } catch (e) {
      console.warn('[cart-drawer] could not parse strings JSON', e);
      this.strings = {};
    }

    // Global open/close hooks
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:updated', (e) => this.render(e.detail));
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-action="cart-open"]');
      if (opener) {
        e.preventDefault();
        this.open();
      }
    });

    // Internal interactions
    this.dialog.addEventListener('click', (e) => this.onDialogClick(e));

    // Intercept add-to-cart form submissions globally
    document.addEventListener('submit', (e) => this.onSubmit(e), true);
  }

  // ---- API ----

  async open() {
    let cart;
    try {
      cart = await this.fetchCart();
    } catch (e) {
      console.error('[cart-drawer] fetch failed', e);
      return;
    }
    this.render(cart);
    if (!this.dialog.open) this.dialog.showModal();
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  async fetchCart() {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('Failed to fetch /cart.js');
    return r.json();
  }

  // Update a line item; key is line.key
  async updateLine(key, quantity) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!r.ok) throw new Error('Failed to update line');
    const cart = await r.json();
    this.broadcast(cart);
    return cart;
  }

  async addItems(items) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.description || 'Failed to add to cart');
    }
    const cart = await this.fetchCart();
    this.broadcast(cart);
    return cart;
  }

  broadcast(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  }

  // ---- Event handlers ----

  onDialogClick(e) {
    const close = e.target.closest('[data-action="cart-close"]');
    if (close) {
      this.close();
      return;
    }
    const item = e.target.closest('[data-line-item]');
    if (!item) return;
    const key = item.dataset.lineKey;

    if (e.target.closest('[data-action="qty-up"]')) {
      const q = Number(item.dataset.qty || 0) + 1;
      this.updateLine(key, q);
    } else if (e.target.closest('[data-action="qty-down"]')) {
      const q = Math.max(0, Number(item.dataset.qty || 0) - 1);
      this.updateLine(key, q);
    } else if (e.target.closest('[data-action="cart-remove"]')) {
      this.updateLine(key, 0);
    }
  }

  async onSubmit(e) {
    const form = e.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;

    e.preventDefault();
    const fd = new FormData(form);
    const id = fd.get('id');
    if (!id) return;
    const quantity = Number(fd.get('quantity') || 1);

    // Collect line-item properties
    const properties = {};
    for (const [k, v] of fd.entries()) {
      if (k.startsWith('properties[')) {
        const propName = k.slice('properties['.length, -1);
        properties[propName] = v;
      }
    }

    try {
      await this.addItems([{ id: Number(id), quantity, properties }]);
      this.open();
    } catch (err) {
      console.error('[cart-drawer]', err);
      // Fallback: submit form normally so user still gets to /cart
      form.submit();
    }
  }

  // ---- Rendering ----

  render(cart) {
    if (!cart) return;
    const isEmpty = !cart.items || cart.items.length === 0;

    this.countTextEl.textContent = isEmpty ? '' : `(${cart.item_count})`;
    this.empty.hidden = !isEmpty;
    this.itemsEl.hidden = isEmpty;
    this.footer.hidden = isEmpty;

    this.itemsEl.innerHTML = '';
    if (!isEmpty) {
      for (const line of cart.items) {
        this.itemsEl.appendChild(this.renderLine(line));
      }
      this.subtotalEl.textContent = this.formatter.format(cart.items_subtotal_price / 100);
      this.renderProgress(cart.items_subtotal_price);
    } else {
      this.progressEl.hidden = true;
    }
  }

  renderLine(line) {
    const node = this.template.content.firstElementChild.cloneNode(true);
    node.dataset.lineKey = line.key;
    node.dataset.qty = line.quantity;

    const mediaLink = node.querySelector('[data-line-url]');
    const titleLink = node.querySelector('[data-line-title-url]');
    if (line.url) {
      mediaLink.href = line.url;
      titleLink.href = line.url;
    }
    const img = node.querySelector('[data-line-image]');
    if (line.image) {
      img.src = line.image.replace('.jpg', '_200x250.jpg').replace('.png', '_200x250.png');
      img.alt = line.product_title || '';
    } else {
      img.remove();
    }

    node.querySelector('[data-line-title]').textContent = line.product_title;

    const variantEl = node.querySelector('[data-line-variant]');
    const variantBits = (line.options_with_values || [])
      .filter((o) => o.value && o.value.toLowerCase() !== 'default title')
      .map((o) => `${o.name}: ${o.value}`)
      .join(' · ');
    if (variantBits) variantEl.textContent = variantBits;
    else variantEl.remove();

    node.querySelector('[data-line-qty]').textContent = line.quantity;
    node.querySelector('[data-line-price]').textContent = this.formatter.format(line.final_line_price / 100);

    if (line.quantity <= 1) {
      const down = node.querySelector('[data-action="qty-down"]');
      if (down) down.disabled = true;
    }

    return node;
  }

  renderProgress(subtotalCents) {
    if (!this.freeThresholdCents || this.freeThresholdCents <= 0) {
      this.progressEl.hidden = true;
      return;
    }
    this.progressEl.hidden = false;
    const pct = Math.min(100, Math.round((subtotalCents / this.freeThresholdCents) * 100));
    this.progressFill.style.width = pct + '%';
    if (subtotalCents >= this.freeThresholdCents) {
      this.progressEl.classList.add('cart-drawer__progress--complete');
      this.progressLabel.innerHTML = this.strings.free_shipping_unlocked || '';
    } else {
      this.progressEl.classList.remove('cart-drawer__progress--complete');
      const remainingCents = this.freeThresholdCents - subtotalCents;
      const tmpl = this.strings.free_shipping_remaining || '';
      this.progressLabel.innerHTML = tmpl.replace('{{ remaining }}', this.formatter.format(remainingCents / 100));
    }
  }
}

if (!customElements.get('medcline-cart-drawer')) {
  customElements.define('medcline-cart-drawer', MedClineCartDrawer);
}
